import React, { useState, useEffect, lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { OrigoAuthenticator } from "@oriola-origo/origo-app-shell";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { rest as RestService } from "@oriola-origo/origo-common-client-lib";
import { StylesProvider } from "@material-ui/core";
import Progress from "@oriola-origo/core/lib/Progress";
import Box from "@oriola-origo/core/lib/Box";
import NotFound from "./components/404/404";
import Layout from "./components/layout";
import { Paths } from "./utils/navigation/navigation";
import {
  Permission,
  ANY_CUSTOMER,
  ProtectedRoute,
  getRolesByCustomerValidity,
  mapGlobalRoles,
} from "./components/auth";
import {
  fetchCustomers,
  userSignIn,
  userSignOut,
  fetchUserDataFromOrigo,
  updateUserData,
  fetchFieldsConfig,
  setTokenData,
} from "./redux";

const CasesView = lazy(() => import("./components/cases"));
const CategorySelection = lazy(() => import("./components/categorySelection"));
const CaseView = lazy(() => import("./components/draft/caseView"));
const SubmittedCaseView = lazy(() => import("./components/case/caseView"));
const EditCaseView = lazy(() => import("./components/editCase/caseView"));
const NewCaseView = lazy(() => import("./components/newCase/newCaseView"));

function App() {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const { userData } = user;
  const { i18n } = useTranslation();
  const [loadingPanel, setLoadingPanel] = useState(true);
  const [restInitialized, setRestInitialized] = useState(false);
  const [loginData, setLoginData] = useState(null);

  if (user.tokenData && user.sessionId) {
    RestService.init(user, res => {
      setRestInitialized(true);
      dispatch(setTokenData(res));
    });
  }

  useEffect(() => {
    if (loginData) {
      dispatch(userSignIn(loginData));

      if (restInitialized) {
        dispatch(fetchFieldsConfig());
        const { userId, organizationIds, rolesByOrgId } = loginData.userData;
        dispatch(fetchCustomers(organizationIds)).then(customers => {
          const valid = getRolesByCustomerValidity(customers, rolesByOrgId);
          const validWithGlobal = mapGlobalRoles(valid);

          dispatch(
            updateUserData({
              ...loginData.userData,
              roles: validWithGlobal.roles,
              rolesByOrgId: validWithGlobal.rolesByOrgId,
            })
          );
          setLoadingPanel(false);
        });

        dispatch(fetchUserDataFromOrigo(userId)).then(origoUserData => {
          if (origoUserData && origoUserData.language) {
            i18n.changeLanguage(origoUserData.language);
          }
        });
      }
    }
  }, [restInitialized, loginData, dispatch, i18n]);

  const renderApp = () => (
    <StylesProvider injectFirst>
      <Layout loadingPanel={loadingPanel}>
        <Suspense
          fallback={
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <Progress />
            </Box>
          }
        >
          <Routes>
            <Route path="/" element={<CasesView />} />
            <Route path={Paths.Cases} element={<CasesView />} />
            <Route
              path={Paths.NewCase}
              element={
                <ProtectedRoute
                  failComponent={NotFound}
                  user={userData}
                  requiredPermission={Permission.CASE_CREATE}
                  customerContext={ANY_CUSTOMER}
                >
                  <CategorySelection />
                </ProtectedRoute>
              }
            />
            <Route
              path={Paths.NewCaseOfType}
              element={
                <ProtectedRoute
                  failComponent={NotFound}
                  user={userData}
                  requiredPermission={Permission.CASE_CREATE}
                  customerContext={ANY_CUSTOMER}
                >
                  <NewCaseView />
                </ProtectedRoute>
              }
            />
            <Route path={Paths.Case} element={<SubmittedCaseView />} />
            <Route path={Paths.Draft} element={<CaseView />} />
            <Route
              path={Paths.EditCase}
              element={
                <ProtectedRoute
                  failComponent={NotFound}
                  user={userData}
                  requiredPermission={Permission.CASE_UPDATE}
                  customerContext={ANY_CUSTOMER}
                >
                  <EditCaseView />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Layout>
    </StylesProvider>
  );

  const renderAuthApp = () => (
    <OrigoAuthenticator
      isSignedIn={user.signedIn}
      onSignedIn={signInData => setLoginData(signInData)}
      onSignedOut={() => {
        dispatch(userSignOut());
      }}
    >
      {renderApp()}
    </OrigoAuthenticator>
  );

  return renderAuthApp();
}

export default App;
